/**
 * Tipos TypeScript para sistema de automação com fluxos visuais
 * Define estruturas de dados para nodes, edges, triggers e configurações
 */

import { Node, Edge } from '@xyflow/react';

/**
 * Tipos de blocos disponíveis no flow builder
 */
/**
 * Tipos de blocos disponíveis no flow builder
 * 
 * RELEASE: 1.1.0 - 2024-12-25 - Adicionado 'trigger_check'
 */
/**
 * RELEASE: 1.2.0 - 2026-02-08 - Adicionado 'message_carousel' para carrossel multi-provider
 */
export type NodeType = 
  | 'start'
  | 'message_text'
  | 'message_media'
  | 'message_template'  // RELEASE: 1.0.0 - 2026-01-31 - Node de template WhatsApp Oficial
  | 'message_carousel'  // RELEASE: 1.0.0 - 2026-02-08 - Node de carrossel multi-provider
  | 'question_buttons'
  | 'question_list'
  | 'input_data'
  | 'condition'
  | 'delay'
  | 'add_tag'
  | 'remove_tag'
  | 'assign_phase'
  | 'update_contact'
  | 'transfer_human'
  | 'transfer_department'
  | 'set_variable'
  | 'webhook'
  | 'goto'
  | 'ab_test'
  | 'filter_array'
  | 'format_array'
  | 'trigger_check'
  | 'pause_bot'
  | 'resume_bot'
  | 'crm_create_deal'
  | 'crm_change_phase'
  | 'crm_assign_seller'
  | 'crm_scheduling_link'  // RELEASE: 1.0.0 - 2026-02-18 - Node de link de agendamento online
  | 'business_hours'
  | 'message_type_check'    // Node de verificação de tipo de mensagem (comentário/DM)
  | 'instagram_message'     // Node de mensagem exclusivo para Instagram
  | 'send_button_link'      // RELEASE: 1.0.0 - 2026-02-19 - Node de botão com link clicável (CTA)
  | 'group'                 // RELEASE: 1.0.0 - 2026-03-07 - Node de grupo/sub-flow para agrupar nodes visualmente
  | 'google_tag_manager'    // RELEASE: 1.0.0 - 2026-03-08 - Node de rastreamento GTM
  | 'meta_pixel'            // RELEASE: 1.0.0 - 2026-03-08 - Node de rastreamento Meta Pixel
  | 'google_analytics'      // RELEASE: 1.0.0 - 2026-03-08 - Node de rastreamento GA4
  | 'embed'                 // RELEASE: 1.0.0 - 2026-03-08 - Node de incorporação de conteúdo externo (iframe)
  | 'send_notification'     // RELEASE: 1.0.0 - 2026-03-12 - Node de envio de notificação para destinatário específico
  | 'contract_generate'     // RELEASE: 1.0.0 - 2026-03-22 - Node de geração automática de contrato
  | 'charge_generate'       // RELEASE: 1.0.0 - 2026-03-22 - Node de geração automática de cobrança financeira
  | 'end_flow'              // RELEASE: 1.0.0 - 2026-05-18 - Node arrastável de encerramento de fluxo (com mensagem de despedida + fechar conversa)
  | 'task_create'           // RELEASE: 1.0.0 - 2026-05-31 - Node de criação automática de tarefa do módulo Tarefas
  | 'task_change_phase'     // RELEASE: 1.0.0 - 2026-05-31 - Node de alteração da fase de uma tarefa existente
  | 'task_assign'           // RELEASE: 1.0.0 - 2026-05-31 - Node de atribuição/remoção do responsável de uma tarefa
  | 'crm_create_activity'   // RELEASE: 1.0.0 - 2026-05-31 - Node de registro de atividade na timeline de um negócio CRM
  | 'connection_query'      // RELEASE: 1.0.0 - 2026-08-29 - Node "Conexão": consulta assíncrona em provider integrado
  | 'end';

/**
 * Configuração do node Encerrar Fluxo (end_flow)
 * RELEASE: 1.0.0 - 2026-05-18
 *
 * Diferente do node `end` (protegido/automático), este é arrastável da palette
 * e permite enviar uma mensagem final + opcionalmente fechar a conversa.
 * Usado especialmente em conjunto com o gatilho `inactivity`.
 */
export interface EndFlowConfig {
  /** ID amigável para identificação */
  nickname?: string;
  /** Mensagem opcional de despedida enviada antes de encerrar */
  farewell_message?: string;
  /** Se true, atualiza conversations.status para conversation_status */
  close_conversation?: boolean;
  /** Status da conversa ao fechar (padrão 'closed') */
  conversation_status?: 'closed' | 'pending' | 'open';
}

/**
 * Modo de destinatário para node de notificação
 * RELEASE: 1.0.0 - 2026-03-12
 */
export type NotificationRecipientMode = 
  | 'fixed'          // Número de telefone fixo digitado manualmente
  | 'variable'       // Variável do fluxo (ex: {{webhook_phone}})
  | 'system_user'    // Usuário/membro do tenant (busca telefone do perfil)
  | 'crm_seller'     // Vendedor CRM específico (busca telefone do vendedor)
  | 'deal_seller';   // Vendedor atribuído ao deal do contexto CRM

/**
 * Representa um único destinatário de notificação
 * Cada destinatário tem seu modo e dados de resolução independentes
 * 
 * RELEASE: 2.0.0 - 2026-03-12 - Suporte a múltiplos destinatários
 */
export interface NotificationRecipient {
  /** Modo de seleção do destinatário */
  mode: NotificationRecipientMode;

  /** Telefone fixo (modo fixed) — formato completo com código de país */
  phone?: string;

  /** Nome da variável com telefone (modo variable) */
  variable?: string;
  /** ID da variável (para VariablePickerCombobox) */
  variable_id?: string;

  /** ID do usuário do sistema (modo system_user) */
  user_id?: string;
  /** Nome do usuário (para exibição no node) */
  user_name?: string;

  /** ID do vendedor CRM (modo crm_seller) */
  seller_id?: string;
  /** Nome do vendedor (para exibição no node) */
  seller_name?: string;
}

/**
 * Configuração do node de envio de notificação
 * Permite enviar mensagem para N destinatários arbitrários (não o contato do fluxo)
 * 
 * RELEASE: 2.0.0 - 2026-03-12 - Suporte a múltiplos destinatários (recipients[])
 * RELEASE: 1.0.0 - 2026-03-12 - Novo node de notificação (campos recipient_* avulsos)
 * 
 * RETROCOMPATIBILIDADE: Se `recipient_mode` existir (config v1), trata como array de 1.
 * Novos saves sempre usam `recipients[]`.
 */
export interface SendNotificationConfig {
  /** Lista de destinatários (v2 — formato preferido) */
  recipients?: NotificationRecipient[];

  // =============================================
  // Campos legados v1 (retrocompatibilidade)
  // Mantidos para configs salvas antes do v2
  // =============================================
  /** @deprecated Use recipients[].mode */
  recipient_mode?: NotificationRecipientMode;
  /** @deprecated Use recipients[].phone */
  recipient_phone?: string;
  /** @deprecated Use recipients[].variable */
  recipient_variable?: string;
  /** @deprecated Use recipients[].variable_id */
  recipient_variable_id?: string;
  /** @deprecated Use recipients[].user_id */
  recipient_user_id?: string;
  /** @deprecated Use recipients[].user_name */
  recipient_user_name?: string;
  /** @deprecated Use recipients[].seller_id */
  recipient_seller_id?: string;
  /** @deprecated Use recipients[].seller_name */
  recipient_seller_name?: string;
  
  /** Inbox obrigatória para envio da notificação */
  inbox_id: string;
  /** Nome da inbox (para exibição no node) */
  inbox_name?: string;
  
  /** Texto da notificação (suporta variáveis {{...}}) — compartilhado entre todos os destinatários */
  message_text: string;
  
  /** Apelido do node */
  nickname?: string;

  // =============================================
  // v2.1.0 — Fallback de template WhatsApp Oficial
  // Se o inbox for whatsapp_oficial e a janela 24h estiver fechada,
  // envia a template ao invés do texto livre (evita erro da Meta e
  // economiza custo quando janela está aberta).
  // =============================================
  /** Liga/desliga a estratégia de fallback via template aprovada */
  use_template_fallback?: boolean;
  /** UUID em whatsapp_templates */
  fallback_template_id?: string;
  /** Nome do template no Meta (ex: "g5flow") */
  fallback_template_name?: string;
  /** Idioma (ex: "pt_BR") */
  fallback_template_language?: string;
  /** Parâmetros do BODY do template (aceita {{variavel}} do fluxo) */
  fallback_template_body_params?: string[];
}

/**
 * Normaliza config de notificação para o formato v2 (recipients[])
 * Se a config usa o formato v1 (recipient_mode), converte para array de 1
 * 
 * @param config - Configuração do node de notificação
 * @returns Lista normalizada de destinatários
 */
export function normalizeNotificationRecipients(config: SendNotificationConfig): NotificationRecipient[] {
  // Formato v2: já tem recipients[]
  if (config.recipients && config.recipients.length > 0) {
    return config.recipients;
  }

  // Formato v1: converte campos avulsos para array de 1
  if (config.recipient_mode) {
    return [{
      mode: config.recipient_mode,
      phone: config.recipient_phone,
      variable: config.recipient_variable,
      variable_id: config.recipient_variable_id,
      user_id: config.recipient_user_id,
      user_name: config.recipient_user_name,
      seller_id: config.recipient_seller_id,
      seller_name: config.recipient_seller_name,
    }];
  }

  // Config vazia
  return [];
}

/**
 * Configuração do node de incorporação (embed/iframe)
 * Permite incorporar site, vídeo, PDF ou qualquer conteúdo via iframe
 * 
 * RELEASE: 1.0.0 - 2026-03-08 - Novo node de incorporação
 */
export interface EmbedNodeConfig {
  /** URL do conteúdo a incorporar (suporta variáveis {{var}}) */
  url: string;
  /** Altura do iframe em pixels (default: 400) */
  height: number;
  /** Apelido do node para exibição */
  nickname?: string;
}

/**
 * Configuração do node Google Tag Manager
 * Dispara evento customizado no dataLayer
 * 
 * RELEASE: 1.0.0 - 2026-03-08 - Novo node de rastreamento
 */
export interface GTMNodeConfig {
  /** Nome do evento a disparar no dataLayer (ex: generate_lead) */
  event_name: string;
  /** Variáveis adicionais enviadas junto ao evento */
  variables: Record<string, string>;
  /** Apelido do node para exibição */
  nickname?: string;
}

/**
 * Tipos de eventos padrão do Meta Pixel
 * RELEASE: 1.0.0 - 2026-03-08
 */
export type MetaPixelEventType = 'PageView' | 'Lead' | 'Purchase' | 'CompleteRegistration' | 'AddToCart' | 'Custom';

/**
 * Configuração do node Meta Pixel
 * Dispara evento de conversão no Facebook Pixel
 * 
 * RELEASE: 1.0.0 - 2026-03-08 - Novo node de rastreamento
 */
export interface MetaPixelNodeConfig {
  /** Tipo de evento padrão ou customizado */
  event_type: MetaPixelEventType;
  /** Nome do evento quando event_type = 'Custom' */
  custom_event_name?: string;
  /** Parâmetros do evento (currency, value, content_name, etc.) */
  params: Record<string, string>;
  /** Apelido do node para exibição */
  nickname?: string;
}

/**
 * Configuração do node Google Analytics 4
 * Dispara evento customizado via gtag
 * 
 * RELEASE: 1.0.0 - 2026-03-08 - Novo node de rastreamento
 */
export interface GA4NodeConfig {
  /** Nome do evento GA4 (ex: sign_up, purchase) */
  event_name: string;
  /** Parâmetros do evento */
  params: Record<string, string>;
  /** Apelido do node para exibição */
  nickname?: string;
}

/**
 * Modo de resposta do node de mensagem Instagram
 * 
 * RELEASE: 1.0.0 - 2025-01-26 - Novo tipo para Instagram
 */
export type InstagramReplyMode = 'public_reply' | 'private_reply' | 'direct_message';

/**
 * Configuração do node de verificação de tipo de mensagem
 * Ramifica o fluxo baseado na origem (comentário ou DM)
 * Saídas fixas: 'comment', 'direct_message', 'default'
 * 
 * RELEASE: 1.0.0 - 2025-01-26 - Novo tipo de node
 */
export interface MessageTypeCheckConfig {
  // Node não precisa de configuração - saídas são fixas
}

/**
 * Configuração do node de mensagem Instagram
 * Suporta três modos de resposta para interações do Instagram
 * 
 * RELEASE: 1.0.0 - 2025-01-26 - Novo tipo de node
 */
export interface InstagramMessageConfig {
  // Modo de resposta: público, privado ou DM direto
  reply_mode: InstagramReplyMode;
  
  // Conteúdo da mensagem (texto)
  text: string;
  
  // Indica se o texto contém variáveis {{...}}
  use_variables?: boolean;
  
  // Mídia (opcional, apenas para DM Direto)
  media_type?: 'image' | 'video' | 'audio' | 'document';
  media_url?: string;
  caption?: string;
}

/**
 * Configuração de um período de horário comercial para um dia da semana
 * RELEASE: 1.0.0 - 2025-01-22 - Novo node de horário comercial
 */
export interface BusinessHoursDay {
  enabled: boolean;           // Se o dia está ativo
  start_time: string;         // Hora início (HH:mm) ex: "08:00"
  end_time: string;           // Hora fim (HH:mm) ex: "18:00"
}

/**
 * Configuração do node de horário comercial
 * Verifica se a hora atual está dentro do horário configurado
 * Possui duas saídas: 'open' (aberto) e 'closed' (fechado)
 * 
 * RELEASE: 1.0.0 - 2025-01-22 - Novo node de horário comercial
 */
export interface BusinessHoursConfig {
  // Horários por dia da semana (chave em inglês minúsculo)
  schedule: {
    sunday: BusinessHoursDay;
    monday: BusinessHoursDay;
    tuesday: BusinessHoursDay;
    wednesday: BusinessHoursDay;
    thursday: BusinessHoursDay;
    friday: BusinessHoursDay;
    saturday: BusinessHoursDay;
  };
  
  // Fuso horário para comparação (ex: "America/Sao_Paulo")
  timezone: string;
}

/**
 * Configuração do node de verificação de gatilho
 * Permite ramificar o fluxo baseado no trigger que o iniciou
 * 
 * RELEASE: 1.0.0 - 2024-12-25 - Novo tipo de node
 */
export interface TriggerCheckConfig {
  // Lista de triggers que este node pode verificar
  // O node terá uma saída (handle) para cada trigger selecionado + 'default'
  triggers_to_check: TriggerType[];
}

/**
 * Configuração do node de pausar bot (Human Takeover)
 * Pausa a automação para permitir atendimento humano
 * 
 * RELEASE: 1.0.0 - 2025-01-26 - Novo tipo de node
 */
export interface PauseBotConfig {
  // Tipo de pausa: indefinida (até ação manual) ou temporizada
  pause_type: 'indefinite' | 'timed';
  // Duração em minutos (apenas para pause_type = 'timed')
  pause_duration?: number;
  // Mensagem opcional a enviar quando pausar
  pause_message?: string;
}

/**
 * Configuração do node de reativar bot
 * Reativa a automação que foi pausada por Human Takeover
 * 
 * RELEASE: 1.0.0 - 2025-01-26 - Novo tipo de node
 */
export interface ResumeBotConfig {
  // Mensagem opcional a enviar quando reativar
  resume_message?: string;
}

/**
 * Configuracao para criar negocio no CRM a partir do fluxo
 * Hierarquia: Workspace → Pipeline → Fase
 * 
 * RELEASE: 1.0.0 - 2025-01-15 - Novo tipo de node CRM
 * RELEASE: 1.1.0 - 2025-01-15 - Adicionado IDs de variaveis para integracao com VariablePickerCombobox
 */
/**
 * Comportamento quando encontrar negócio duplicado
 * RELEASE: 2.0.0 - 2025-01-15 - Novo enum para verificação de duplicidade
 */
export type CrmDuplicateBehavior = 
  | 'always_create'           // Ignora verificação, sempre cria (comportamento atual)
  | 'skip_if_open'            // Pula criação, retorna ID do existente
  | 'update_phase'            // Move negócio existente para a fase configurada
  | 'create_if_all_closed';   // Só cria se não houver nenhum aberto no escopo

/**
 * Unidade de tempo para janela de recorrência
 * RELEASE: 2.0.0 - 2025-01-15
 */
export type RecurrenceWindowUnit = 'days' | 'weeks' | 'months';

/**
 * Configuracao para criar negocio no CRM a partir do fluxo
 * Hierarquia: Workspace → Pipeline → Fase
 * 
 * RELEASE: 1.0.0 - 2025-01-15 - Novo tipo de node CRM
 * RELEASE: 1.1.0 - 2025-01-15 - Adicionado IDs de variaveis para integracao com VariablePickerCombobox
 * RELEASE: 2.0.0 - 2025-01-15 - Adicionado verificação de duplicidade com escopo, janela de recorrência e comportamento
 */
export interface CrmCreateDealConfig {
  // Hierarquia: Workspace → Pipeline → Fase (obrigatorios)
  workspace_id: string;
  workspace_name: string;
  pipeline_id: string;
  pipeline_name: string;
  phase_id: string;
  phase_name: string;
  phase_color?: string;
  
  // Origem do deal (obrigatorio) - ID de LEAD_SOURCES
  source: string;
  source_label?: string;
  
  // ========================================
  // ATRIBUIÇÃO DE VENDEDOR (v3.0.0)
  // ========================================
  
  // Modo de atribuição do vendedor
  seller_assignment_mode?: 'manual' | 'distribution_queue' | 'none';
  
  // Vendedor responsavel (quando seller_assignment_mode = 'manual')
  seller_id?: string;
  seller_name?: string;
  
  // Fila de distribuição (quando seller_assignment_mode = 'distribution_queue')
  distribution_queue_id?: string;
  distribution_queue_name?: string;
  distribution_queue_method?: string; // round_robin, least_busy, weighted
  
  // Titulo do deal
  title_mode: 'static' | 'from_variable' | 'auto';
  title_static?: string;     // Quando title_mode = 'static'
  title_variable?: string;   // Nome da variavel quando title_mode = 'from_variable'
  title_variable_id?: string; // ID da variavel (novo) - para integracao com VariablePickerCombobox
  // auto = gera titulo automatico: "[Nome do Contato] - DD/MM/YYYY"
  
  // Variavel para armazenar ID do deal criado (opcional)
  output_variable?: string;    // Nome da variavel
  output_variable_id?: string; // ID da variavel (novo) - para integracao com VariablePickerCombobox
  
  // ========================================
  // VERIFICAÇÃO DE DUPLICIDADE (v2.0.0)
  // ========================================
  
  // Habilita verificação de duplicidade antes de criar
  duplicate_check_enabled?: boolean;
  
  // Escopo de verificação (multi-select) - define ONDE verificar se já existe negócio
  check_workspace_ids?: string[];    // IDs dos workspaces para verificar
  check_workspace_names?: string[];  // Nomes para exibição
  check_pipeline_ids?: string[];     // IDs dos pipelines para verificar
  check_pipeline_names?: string[];   // Nomes para exibição
  check_phase_ids?: string[];        // IDs das fases para verificar
  check_phase_names?: string[];      // Nomes para exibição
  
  // Janela de Recorrência: "Se o contato voltar em até X [dias/semanas/meses], é o mesmo negócio"
  recurrence_window_value?: number;              // Ex: 30
  recurrence_window_unit?: RecurrenceWindowUnit; // Ex: 'days'
  
  // Comportamento quando encontrar duplicata
  duplicate_behavior?: CrmDuplicateBehavior;
  
  // ========================================
  // VARIÁVEL DE RESULTADO DE DUPLICIDADE (v2.1.0)
  // ========================================
  
  // Variável para armazenar resultado da verificação de duplicidade
  // Valores possíveis: 'created' | 'skipped' | 'phase_updated'
  duplicate_result_variable?: string;      // Nome da variável
  duplicate_result_variable_id?: string;   // ID da variável (para VariablePickerCombobox)
  
  // ========================================
  // ATRIBUIÇÃO DA CONVERSA AO VENDEDOR (v4.0.0)
  // ========================================
  
  // Quando habilitado, atribui conversations.assigned_to ao mesmo vendedor do deal
  assign_conversation_to_seller?: boolean;
  
  // ========================================
  // PRODUTOS/SERVIÇOS PRÉ-CONFIGURADOS (v5.0.0)
  // ========================================
  
  /** Lista de produtos/serviços para inserir automaticamente ao criar o deal */
  products?: Array<{
    /** ID do produto no catálogo (crm_products) */
    product_id: string;
    /** Nome do produto (para exibição na config do node) */
    product_name: string;
    /** Quantidade */
    quantity: number;
    /** Preço unitário */
    unit_price: number;
    /** Percentual de desconto (0-100) */
    discount_percent?: number;
    /** Tipo do produto (para ícone) */
    product_type?: 'product' | 'service';
    /** SKU do produto (para exibição) */
    sku?: string | null;
  }>;
}

/**
 * Configuracao para alterar fase de negocio CRM
 * Move deal para outra fase do mesmo pipeline
 * 
 * RELEASE: 1.0.0 - 2025-01-15 - Novo tipo de node CRM
 * RELEASE: 1.1.0 - 2025-01-15 - Adicionado ID de variavel para integracao com VariablePickerCombobox
 * RELEASE: 1.2.0 - 2026-02-18 - Adicionado from_trigger para usar deal do gatilho CRM/Contrato
 */
export interface CrmChangePhaseConfig {
  // Estrategia para encontrar o deal
  // from_trigger: usa o deal que disparou o gatilho CRM ou Evento de Contrato
  // from_variable: usa o ID do deal armazenado em uma variavel do fluxo
  // most_recent: busca o deal mais recente do contato com filtro de status
  deal_selection: 'most_recent' | 'from_variable' | 'from_trigger';
  deal_variable_name?: string;  // Nome da variavel quando deal_selection = 'from_variable'
  deal_variable_id?: string;    // ID da variavel (novo) - para integracao com VariablePickerCombobox
  
  // Filtro de status para busca most_recent
  // Aplica-se apenas quando deal_selection = 'most_recent'
  deal_status_filter: 'open' | 'won' | 'lost' | 'all';
  
  // Fase de destino (mesmo pipeline do deal)
  phase_id: string;
  phase_name: string;
  phase_color?: string;
  
  // Informacoes para exibicao no node (somente leitura)
  pipeline_name?: string;
  is_won?: boolean;
  is_lost?: boolean;
}

/**
 * Configuracao para atribuir vendedor a negocio CRM existente
 * Suporta atribuicao manual (vendedor especifico) ou via fila de distribuicao
 * 
 * RELEASE: 1.0.0 - 2025-01-25 - Novo tipo de node CRM
 * RELEASE: 1.1.0 - 2026-02-18 - Adicionado from_trigger para usar deal do gatilho CRM
 */
export interface CrmAssignSellerConfig {
  // ========================================
  // SELECAO DO DEAL (reutiliza logica do crm_change_phase)
  // ========================================
  deal_selection: 'most_recent' | 'from_variable' | 'from_trigger';
  deal_variable_name?: string;
  deal_variable_id?: string;
  deal_status_filter: 'open' | 'won' | 'lost' | 'all';
  
  // ========================================
  // MODO DE ATRIBUICAO (reutiliza logica do crm_create_deal)
  // ========================================
  seller_assignment_mode: 'manual' | 'distribution_queue';
  
  // Vendedor especifico (quando mode = 'manual')
  seller_id?: string;
  seller_name?: string;
  
  // Fila de distribuicao (quando mode = 'distribution_queue')
  distribution_queue_id?: string;
  distribution_queue_name?: string;
  distribution_queue_method?: string;
  
  // Forca redistribuicao mesmo para negocios com vendedor
  // Quando true: substitui vendedor existente pelo novo da fila
  // Quando false ou undefined: mantem vendedor existente (idempotencia)
  force_redistribute?: boolean;
}

/**
 * Configuração do node de geração de link de agendamento online
 * Gera link único /schedule/{tenant_slug}/{event_slug}?deal_id={deal_id}
 * 
 * RELEASE: 1.0.0 - 2026-02-18 - Novo node CRM agendamento online
 */
export interface CrmSchedulingLinkConfig {
  // Tipo de evento de agendamento selecionado
  event_type_id: string;
  event_type_slug: string;
  event_type_title: string;
  event_type_color?: string;
  event_type_duration_minutes?: number;
  
  // Origem do deal_id para compor o link
  deal_selection: 'most_recent' | 'from_variable' | 'from_trigger';
  deal_variable_name?: string;   // Quando deal_selection = 'from_variable'
  
  // Variável de saída onde o link completo será salvo
  output_variable: string;
  output_variable_id?: string; // ID da variável para rastreabilidade (mesmo padrão do CrmCreateDealConfig)
}

/**
 * Variante do Teste A/B
 */
export interface ABTestVariant {
  id: string;        // 'variant_0', 'variant_1', etc.
  label: string;     // 'A', 'B', 'C', 'D' (fixo)
  percentage: number; // 0-100
}

/**
 * Configuração de Teste A/B
 * Distribui fluxo entre múltiplas saídas por porcentagem
 */
export interface ABTestConfig {
  variants: ABTestVariant[];
}

/**
 * Configuração de mensagem de texto
 */
export interface MessageTextConfig {
  content: string;
  useVariables: boolean;
}

/**
 * Configuração de mensagem com mídia
 * 
 * RELEASE: 1.2.0 - 2026-02-03 - Adicionado suporte a mídias da galeria interna
 */
export interface MessageMediaConfig {
  mediaType: 'image' | 'video' | 'audio' | 'document';
  mediaUrl: string;
  caption?: string;
  
  // RELEASE: 4.1.0 - Canal específico para envio
  inbox_id?: string;
  inbox_name?: string;
  
  // RELEASE: 1.2.0 - Campos para rastreamento de mídia da galeria
  // Origem da mídia: 'url' para externa, 'gallery' para galeria interna
  media_source?: 'url' | 'gallery';
  // UUID do registro em media_gallery (quando media_source = 'gallery')
  media_gallery_id?: string;
  // Nome do arquivo para match no import (quando media_source = 'gallery')
  media_gallery_name?: string;
}

/**
 * Configuração do node de template WhatsApp Oficial (HSM)
 * Permite enviar templates pré-aprovados para iniciar conversas fora da janela 24h
 * 
 * RELEASE: 1.0.0 - 2026-01-31 - Novo node de template
 */
export interface MessageTemplateConfig {
  // ========================================
  // SELEÇÃO DO CANAL (obrigatório)
  // Templates REQUEREM inbox do WhatsApp Oficial
  // ========================================
  inbox_id: string;
  inbox_name?: string;  // Para exibição no node
  
  // ========================================
  // TEMPLATE SELECIONADO
  // ========================================
  template_id: string;         // UUID do registro em whatsapp_templates
  template_name: string;       // Nome do template no Meta (ex: "g5flow")
  template_language: string;   // Idioma (ex: "pt_BR")
  template_category?: string;  // UTILITY, MARKETING, AUTHENTICATION
  
  // ========================================
  // VARIÁVEIS DO BODY
  // Formato: { "1": "{{contact_name}}", "2": "valor fixo" }
  // Chave = número da variável, Valor = texto fixo ou {{variável}}
  // ========================================
  body_variables: Record<string, string>;
  
  // ========================================
  // VARIÁVEIS DO HEADER (opcional)
  // Para templates com HEADER dinâmico (texto ou mídia)
  // ========================================
  header_type?: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  header_variable?: string;    // Texto ou URL da mídia (suporta {{variavel}})
  
  // ========================================
  // VARIÁVEIS DOS BUTTONS (opcional)
  // Para botões URL com {{1}} ou ORDER_DETAILS (PIX)
  // ========================================
  button_variables?: Array<{
    index: number;
    type: 'url' | 'order_details';
    // Para URL dinâmica
    url_suffix?: string;       // Sufixo da URL (suporta {{variavel}})
    // Para ORDER_DETAILS (PIX)
    pix_code?: string;
    merchant_name?: string;
    pix_key?: string;
    pix_key_type?: 'CNPJ' | 'CPF' | 'EMAIL' | 'CELULAR' | 'ALEATORIA';
    amount_value?: string;     // String para suportar {{variavel}}
    item_name?: string;
    reference_id?: string;
  }>;
  
  // ========================================
  // PREVIEW (somente leitura)
  // Armazenado para exibição no node sem refetch
  // ========================================
  template_body_text?: string;   // Texto do BODY para preview
  template_has_buttons?: boolean;
}

/**
 * Configuração de botão de carrossel
 * Suporta 3 tipos: URL (abre link), REPLY (resposta rápida), CALL (inicia chamada)
 * 
 * RELEASE: 1.0.0 - 2026-02-08 - Novo tipo para carrossel multi-provider
 */
export interface CarouselButtonConfig {
  id: string;                    // UUID do botão
  type: 'URL' | 'REPLY' | 'CALL'; // Tipo de ação
  label: string;                 // Texto do botão (obrigatório, máx ~20 chars)
  url?: string;                  // URL para tipo URL
  phone?: string;                // Telefone para tipo CALL (formato: 5511999999999)
}

/**
 * Configuração de um cartão do carrossel
 * Cada cartão contém imagem, texto e até 3 botões
 * 
 * RELEASE: 1.0.0 - 2026-02-08 - Novo tipo para carrossel multi-provider
 */
export interface CarouselCardConfig {
  id: string;                    // UUID do cartão
  image_url: string;             // URL da imagem (obrigatório)
  image_source?: 'url' | 'gallery'; // Origem da imagem
  media_gallery_id?: string;     // ID da galeria (quando image_source = 'gallery')
  text: string;                  // Texto do cartão (obrigatório, suporta variáveis)
  buttons?: CarouselButtonConfig[]; // Botões do cartão (0-3)
}

/**
 * Configuração do node de carrossel (Carousel Message)
 * Envia múltiplos cartões com imagem, texto e botões interativos
 * 
 * RELEASE: 1.0.0 - 2026-02-08 - Novo node de carrossel multi-provider
 * RELEASE: 1.1.0 - 2026-02-08 - Suporte nativo a Meta WABA
 * 
 * Limitações por provider:
 * - Z-API: ✅ Suportado (1-10 cartões, 0-3 botões por cartão)
 * - Meta WABA: ✅ Suportado (2-10 cartões, 1 URL ou 1-2 Quick Reply por cartão)
 * - Notificame: ✅ Via Meta (segue regras Meta WABA)
 * - Evolution: ⏳ Futuro
 * - WAHA: ⏳ Futuro
 */
export interface MessageCarouselConfig {
  // Texto principal do carrossel (exibido acima dos cartões)
  message: string;
  
  // Array de cartões (mínimo 1, recomendado 2-10)
  cards: CarouselCardConfig[];
  
  // Canal de envio (obrigatório para gatilhos CRM)
  inbox_id?: string;
  inbox_name?: string;
}

/**
 * Ação quando resposta inválida é recebida
 */
export type InvalidResponseAction = 'repeat' | 'message_then_repeat' | 'continue' | 'end' | 'goto_node';

/**
 * Configuração de resposta inválida para perguntas
 */
export interface InvalidResponseConfig {
  enabled: boolean;                         // Se o tratamento está habilitado
  action: InvalidResponseAction;            // Ação a executar
  message?: string;                         // Mensagem personalizada para resposta inválida
  max_retries?: number;                     // Número máximo de tentativas (default: 3)
  goto_node_id?: string;                    // ID do node destino (quando action = 'goto_node')
  exceeded_action?: 'continue' | 'end' | 'goto_node'; // Ação após exceder tentativas
  exceeded_goto_node_id?: string;           // ID do node destino após exceder tentativas
}

/**
 * Configuração de pergunta com botões
 */
export interface QuestionButtonsConfig {
  text: string; // Texto da pergunta
  options: Array<{ id: string; label: string; value?: string }>; // Opções de resposta
  variable_id?: string; // ID da variável para salvar resposta
  variable_name?: string; // Nome da variável para salvar resposta
  fallback_enabled?: boolean; // Forçar menu numérico ao invés de botões nativos
  timeout?: number;
  timeoutAction?: 'repeat' | 'next' | 'end';
  invalid_response?: InvalidResponseConfig; // Configuração de resposta inválida
}

/**
 * Configuração de pergunta com lista
 * 
 * RELEASE: 2.0.0 - 2024-12-24 - Adicionado suporte a lista dinâmica via variável
 * RELEASE: 2.1.0 - 2024-12-24 - Adicionado options_distinct para agrupar valores únicos
 */
export interface QuestionListConfig {
  text: string; // Texto da pergunta
  options: Array<{ id: string; label: string; title?: string; description?: string; value?: string }>; // Opções de resposta (estáticas)
  variable_id?: string; // ID da variável para salvar resposta
  variable_name?: string; // Nome da variável para salvar resposta
  fallback_enabled?: boolean; // Forçar menu numérico ao invés de lista nativa
  buttonText?: string;
  invalid_response?: InvalidResponseConfig; // Configuração de resposta inválida
  
  // Campos para lista dinâmica (opções vindas de variável - ex: resposta de webhook)
  // RELEASE: 2.2.0 - 2024-12-28 - Adicionado options_array_path para navegação em objetos aninhados
  options_source?: 'static' | 'variable'; // Fonte das opções: estático ou variável
  options_variable?: string;              // Nome da variável que contém o objeto/array de opções
  options_array_path?: string;            // Caminho até o array dentro do objeto (ex: "veiculos", "data.items", "response.dados.lista")
  options_label_path?: string;            // Caminho no objeto para extrair o label (ex: "veiculo_marca")
  options_value_path?: string;            // Caminho no objeto para extrair o value (ex: "veiculo_placa")
  options_description_path?: string;      // Caminho no objeto para extrair a descrição (opcional)
  options_distinct?: boolean;             // Se true (padrão), remove duplicatas baseado no value
}

/**
 * Configuração de coleta de dados
 */
export interface InputDataConfig {
  variable_name: string;
  data_type: 'text' | 'number' | 'email' | 'phone' | 'cpf' | 'cnpj' | 'date';
  question: string;
  validation_message?: string;
  // RELEASE 4.2.0 - Suporte a seleção manual de Inbox (para gatilhos CRM sem conversa ativa)
  inbox_id?: string;
  // RELEASE 4.2.0 - Quando true, o node NÃO envia mensagem de pergunta; apenas parqueia
  // a execução em waiting_input aguardando a próxima mensagem do cliente.
  wait_only?: boolean;
  // RELEASE 4.5.0 - Timeout de espera pela resposta.
  // Reaproveita infra de scheduled_resume_at + flow-scheduler (mesmo padrão do Delay).
  response_timeout_enabled?: boolean;
  response_timeout_value?: number;               // valor inteiro >= 1
  response_timeout_unit?: 'minutes' | 'hours' | 'days';
  // RELEASE 4.6.0 - Nome da variável global que recebe 'responded' | 'timeout'
  // (mesmo padrão do node Gerar Cobrança). Preferido em relação ao campo legado abaixo.
  response_timeout_result_variable?: string;
  /** @deprecated Use response_timeout_result_variable. Mantido para retrocompat de fluxos antigos. */
  response_timeout_default_value?: string;
}

/**
 * Opções de correspondência para normalização de valores em condições
 */
export interface MatchOptions {
  ignoreCase: boolean;      // Ignorar maiúsculas/minúsculas
  ignoreAccents: boolean;   // Ignorar acentos (café = cafe)
  trimSpaces: boolean;      // Remover espaços extras
}

/**
 * Configuração de condição (estilo Typebot com múltiplos IFs)
 * Permite múltiplos blocos IF, cada um com várias condições combinadas
 * Se nenhum IF for verdadeiro, segue para o ELSE
 */
export interface ConditionConfig {
  comparisons: Array<{
    id: string; // UUID único para o IF
    conditions: Array<{
      id: string; // UUID único para a condição
      variable: string;
      operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'starts_with' | 'ends_with' | 'greater' | 'less' | 'greater_equal' | 'less_equal' | 'is_set' | 'is_empty' | 'regex';
      value: string;           // Valor único (compatibilidade legada)
      values?: string[];       // Múltiplos valores aceitos
      matchOptions?: MatchOptions; // Opções de normalização
    }>;
    logicOperator: 'and' | 'or'; // Combina condições dentro do mesmo IF
  }>;
}

/**
 * Configuração de delay
 */
export interface DelayConfig {
  duration: number;
  unit: 'seconds' | 'minutes' | 'hours';
}

/**
 * Configuração de adicionar tag
 * Suporta tags de contato e negócio (CRM)
 * 
 * RELEASE: 1.0.0 - Original (somente contato)
 * RELEASE: 2.0.0 - 2025-01-15 - Adicionado suporte a tags de negócio (CRM)
 */
export interface AddTagConfig {
  // Escopo: contato ou negócio (CRM) - padrão 'contact' para retrocompatibilidade
  scope?: 'contact' | 'deal';
  
  // Tag a adicionar
  tag_id?: string;
  tag_name?: string;
  tag_color?: string;
  
  // Identificação do deal (quando scope = 'deal')
  deal_selection?: 'most_recent' | 'from_variable';
  deal_variable_id?: string;
  deal_variable_name?: string;
  deal_status_filter?: 'open' | 'won' | 'lost' | 'all';
  
  // Campos legados (manter para retrocompatibilidade)
  tagId?: string;
  tagName?: string;
}

/**
 * Configuração de remover tag
 * Suporta tags de contato e negócio (CRM)
 * 
 * RELEASE: 1.0.0 - 2025-01-15 - Novo node
 */
export interface RemoveTagConfig {
  // Escopo: contato ou negócio
  scope: 'contact' | 'deal';
  
  // Modo de remoção
  mode: 'specific' | 'all';
  
  // Tag específica (quando mode = 'specific')
  tag_id?: string;
  tag_name?: string;
  tag_color?: string;
  
  // Identificação do deal (quando scope = 'deal')
  deal_selection?: 'most_recent' | 'from_variable';
  deal_variable_id?: string;
  deal_variable_name?: string;
  deal_status_filter?: 'open' | 'won' | 'lost' | 'all';
}

/**
 * Configuração de atribuir fase do Kanban
 * Permite definir a fase da conversa para visualização no quadro
 * 
 * RELEASE: 1.0.0 - 2024-12-26 - Novo tipo de node
 */
export interface AssignPhaseConfig {
  category_id: string;
  category_name: string;
  phase_id: string;
  phase_name: string;
}

/**
 * Configuração de atualizar contato
 */
export interface UpdateContactConfig {
  fields: Array<{
    fieldName: string;
    value: string;
    useVariable: boolean;
  }>;
}

/**
 * Configuração de transferência humana
 * 
 * RELEASE: 2.0.0 - 2024-12-26 - Adicionado assignment_mode e fallback_strategy
 * RELEASE: 2.1.0 - 2024-12-30 - Adicionado skip_if_assigned (evitar sobrescrita)
 * RELEASE: 2.2.0 - 2025-01-25 - Adicionado modo distribution_queue
 * 
 * Suporta três modos:
 * - 'manual': Seleciona agente específico (comportamento original)
 * - 'previous_agent': Busca último agente que atendeu a conversa
 * - 'distribution_queue': Usa fila de distribuição global para atribuição automática
 */
export interface TransferHumanConfig {
  // Modo de atribuição (padrão: 'manual' para compatibilidade)
  assignment_mode?: 'manual' | 'previous_agent' | 'distribution_queue';
  
  // Campos para modo 'manual' (comportamento original)
  agent_id?: string;
  agent_name?: string;
  
  // Estratégia de fallback quando último agente indisponível (modo 'previous_agent')
  fallback_strategy?: 'pick_any_active' | 'none';
  
  // Se true, não sobrescreve agente já atribuído à conversa
  // Útil para evitar conflitos com distribuição automática ou atribuição manual
  skip_if_assigned?: boolean;
  
  // Campos para modo 'distribution_queue' (v2.2.0)
  distribution_queue_id?: string;
  distribution_queue_name?: string;
  distribution_queue_method?: string;
  
  // Força redistribuição mesmo se conversa já tiver agente (modo distribution_queue)
  force_redistribute?: boolean;
}

/**
 * Configuração de transferência para departamento
 */
export interface TransferDepartmentConfig {
  department_id: string;
  department_name: string;
}

/**
 * Configuração de data dinâmica para SetVariable
 * Permite calcular datas automaticamente baseadas na data de execução
 * 
 * RELEASE: 1.0.0 - 2025-12-28 - Novo tipo para cálculo dinâmico de datas
 */
export interface DynamicDateConfig {
  base: 'today' | 'start_of_month' | 'end_of_month' | 'start_of_year' | 'end_of_year';
  offset_type: 'none' | 'days' | 'months' | 'years';
  offset_value: number; // Positivo = futuro, Negativo = passado
  format: string; // Formato de saída (ex: "dd/MM/yyyy", "yyyy-MM-dd")
}

/**
 * Configuração de definir variável
 * 
 * RELEASE: 1.1.0 - 2025-12-28 - Adicionado value_type 'dynamic_date' e date_config
 */
export interface SetVariableConfig {
  variable_id: string;
  variable_name: string;
  value_type: 'static' | 'variable' | 'expression' | 'dynamic_date';
  value: string;
  // Configuração de data dinâmica (quando value_type = 'dynamic_date')
  date_config?: DynamicDateConfig;
}

/**
 * Mapeamento de campo da resposta para variável do fluxo
 * Permite extrair valores específicos da resposta e salvar em variáveis
 */
export interface ResponseFieldMapping {
  id: string;                // ID único do mapeamento
  responsePath: string;      // Caminho no JSON (ex: "data.veiculo_marca", "[0].cliente")
  variableName: string;      // Nome da variável onde salvar
}

/**
 * Condição para considerar erro na resposta do webhook
 * Permite definir múltiplas condições que indicam falha
 */
export interface WebhookErrorCondition {
  type: 'http_status' | 'json_path_empty' | 'json_path_equals';
  path?: string;           // Caminho JSON para verificar (ex: "data", "error")
  value?: string;          // Valor para comparar (quando type = 'json_path_equals')
  http_status?: number[];  // Lista de status HTTP para considerar erro (ex: [404, 400])
}

/**
 * Configuração de tratamento de erro do webhook
 * Permite configurar retry quando API retorna erro ou dados não encontrados
 * 
 * RELEASE: 1.0.0 - 2024-12-25 - Novo tratamento de erro com retry
 * RELEASE: 1.1.0 - 2024-12-25 - Adicionado exceeded_message para mensagem de encerramento
 */
export interface WebhookErrorHandling {
  enabled: boolean;                                          // Se o tratamento está habilitado
  error_conditions: WebhookErrorCondition[];                 // Condições para considerar erro
  action: 'repeat_previous' | 'message_and_repeat' | 'goto_node' | 'continue';  // Ação quando erro
  message?: string;                                          // Mensagem ao usuário (quando action = message_and_repeat)
  max_retries?: number;                                      // Máximo de tentativas (default: 3)
  exceeded_action?: 'continue' | 'end' | 'goto_node';        // Ação após exceder tentativas
  exceeded_message?: string;                                 // Mensagem ao encerrar por exceder tentativas
  input_node_id?: string;                                    // ID do node de input a repetir
  exceeded_goto_node_id?: string;                            // ID do node destino após exceder tentativas
}

/**
 * Configuração de webhook
 * Suporta injeção segura de credenciais via tenant_credentials
 * 
 * RELEASE: 1.2.0 - 2024-12-24 - Adicionado responseMapping e testValues
 * RELEASE: 2.0.0 - 2024-12-25 - Adicionado error_handling para tratamento de erro com retry
 */
export interface WebhookConfig {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: Record<string, string>;
  body?: string;
  responseVariable?: string;
  credential_id?: string; // ID da credencial em tenant_credentials (API Key, Bearer Token, etc.)
  
  // Mapeamento de campos da resposta para variáveis do fluxo
  responseMapping?: ResponseFieldMapping[];
  
  // Valores de teste para variáveis (usado apenas no modal de configuração)
  testValues?: Record<string, string>;
  
  // Tratamento de erro da API com retry
  error_handling?: WebhookErrorHandling;
}

/**
 * Configuração de pular para outro node
 */
export interface GotoConfig {
  target_node_id: string;    // ID do node destino
  target_node_label: string; // Label para exibição
}

/**
 * Tipo de operador para filtro de array
 */
export type FilterOperator = 'equals' | 'not_equals' | 'contains' | 'not_contains' | 
  'starts_with' | 'ends_with' | 'greater' | 'less' | 'greater_equal' | 'less_equal' | 
  'is_set' | 'is_empty';

/**
 * Uma condição individual de filtro
 * 
 * RELEASE: 2.0.0 - 2024-12-30 - Novo formato para múltiplas condições
 */
export interface FilterArrayCondition {
  id: string;                    // UUID único da condição
  field_path: string;            // Caminho do campo a verificar (ex: "status")
  operator: FilterOperator;      // Operador de comparação
  value: string;                 // Valor para comparar
}

/**
 * Configuração de filtrar array
 * Permite filtrar itens de um array que satisfazem uma ou mais condições
 * 
 * RELEASE: 1.0.0 - 2024-12-24 - Novo node para filtrar arrays de webhooks
 * RELEASE: 2.0.0 - 2024-12-30 - Suporte a múltiplas condições com E/OU
 */
export interface FilterArrayConfig {
  source_variable: string;       // Variável que contém o array de entrada
  output_variable: string;       // Variável onde salvar o array filtrado
  
  // NOVO: Múltiplas condições com operador lógico
  conditions?: FilterArrayCondition[];   // Array de condições
  logicOperator?: 'and' | 'or';          // Operador lógico entre condições (padrão: 'and')
  
  // LEGADO: Mantido para compatibilidade com flows existentes
  field_path?: string;           // Caminho do campo a verificar (ex: "status")
  operator?: FilterOperator;     // Operador de comparação
  filter_value?: string;         // Valor para comparar
}

/**
 * Configuração de formatar array
 * Transforma um array em texto formatado usando template
 * Suporte opcional para enviar documentos (PDFs) diretamente via WhatsApp
 * 
 * RELEASE: 1.0.0 - 2024-12-24 - Novo node para formatar arrays em mensagens
 * RELEASE: 2.0.0 - 2024-12-25 - Adicionado suporte a envio de documentos (send_documents)
 */
export interface FormatArrayConfig {
  source_variable: string;       // Variável que contém o array de entrada
  output_variable: string;       // Variável onde salvar o texto formatado
  item_template: string;         // Template para cada item (ex: "📄 Boleto {{$index}}\nCódigo: {{codigo_barras}}")
  separator?: string;            // Separador entre itens (padrão: "\n\n")
  filter_field?: string;         // Campo para filtrar antes de formatar (opcional)
  filter_value?: string;         // Valor do filtro (pode usar variável: {{var}})
  header_template?: string;      // Texto antes da lista (opcional)
  footer_template?: string;      // Texto depois da lista (opcional)
  empty_message?: string;        // Mensagem se array vazio (padrão: "Nenhum item encontrado.")
  
  // Configuração para enviar documentos em vez de links
  send_documents?: boolean;              // Se true, envia arquivos em vez de texto formatado
  document_url_field?: string;           // Campo do array que contém a URL do documento (ex: "boleto_link")
  document_caption_template?: string;    // Template para legenda do documento (ex: "Boleto {{$index}} - R$ {{valor}}")
  document_filename_template?: string;   // Template para nome do arquivo (ex: "boleto_{{$index}}.pdf")
  
  // NOVO: Configuração de limite e ordenação do array
  limit_items?: number;                  // Quantidade máxima de itens a processar (0 ou undefined = todos)
  sort_field?: string;                   // Campo para ordenação (ex: "vencimento", "valor")
  sort_direction?: 'asc' | 'desc';       // Direção: asc (crescente/mais antigo) ou desc (decrescente/mais recente)
}

/**
 * Dados customizados do node do React Flow
 */
export interface FlowNodeData extends Record<string, unknown> {
  type: NodeType;
  label: string;
  config: Record<string, any>;
  description?: string;
}

/**
 * Node do React Flow tipado
 */
export type FlowNode = Node<FlowNodeData>;

/**
 * Edge do React Flow tipado
 */
export type FlowEdge = Edge;

/**
 * Tipo de gatilho de automação
 */
/**
 * Tipo de gatilho de automação
 * 
 * RELEASE: 1.1.0 - 2024-12-25 - Adicionado 'active_conversation'
 * RELEASE: 1.2.0 - 2024-12-26 - Adicionado 'any_message' (fallback universal)
 * RELEASE: 1.3.0 - 2026-01-25 - Adicionado 'crm_filter' (filtro avançado CRM)
 * RELEASE: 1.4.0 - 2026-02-18 - Adicionado 'contract_event' (eventos de contrato digital)
 */
export type TriggerType = 
  | 'keyword'
  | 'first_message'
  | 'scheduled'
  | 'inactivity'
  | 'event'
  | 'webhook'
  | 'contact_tag'
  | 'phase'
  | 'department'
  | 'active_conversation'
  | 'any_message'
  | 'crm_filter'
  | 'contract_event'
  // RELEASE: 1.5.0 - 2026-06-05 - Gatilho público "Campanha":
  // habilita o fluxo a ser invocado por ações pós-disparo de campanhas.
  | 'campaign_action';
  // RELEASE: 1.7.0 - 2026-08-30 - Removido 'workflow_stage_entered' (Workflow v1 descontinuado)


/**
 * Configuração do gatilho de eventos de contrato digital
 * Permite disparar fluxos baseado em eventos do ciclo de vida de contratos
 * (assinatura, recusa, visualização, finalização, etc.)
 * 
 * RELEASE: 1.0.0 - 2026-02-18 - Novo tipo de gatilho para contratos digitais
 */
export interface ContractEventTriggerConfig {
  // Evento do contrato que dispara o gatilho
  contract_event_type:
    | 'contract_sent'         // Contrato enviado para assinatura
    | 'contract_signed_partial'  // Um signatário assinou (configurável por papel)
    | 'contract_signed_all'      // Todos assinaram (document.finished)
    | 'contract_refused'         // Algum signatário recusou
    | 'contract_expired'         // Contrato expirou
    | 'contract_cancelled'       // Contrato cancelado/deletado
    | 'contract_viewed';         // Signatário visualizou

  // Filtro de papéis de signatário (apenas para signed_partial, refused e viewed)
  // Se vazio, dispara para QUALQUER papel
  signer_roles?: string[];  // ex: ['contratante', 'representante', 'testemunha']

  // Filtro por template (opcional - se vazio, qualquer template dispara)
  template_id?: string;
  template_name?: string;
}

/**
 * Configuração do gatilho de filtro avançado CRM

 * Permite disparar fluxos baseado em filtros complexos de deals/conversas
 * com verificação de tempo no status atual
 * 
 * RELEASE: 1.0.0 - 2026-01-25 - Novo tipo de gatilho baseado em tempo
 * RELEASE: 1.1.0 - 2026-01-25 - Adicionados filtros para conversas: status, departamento, kanban
 * RELEASE: 1.3.0 - 2026-05-31 - Adicionado suporte a entity_type 'task' (Tarefas)
 *                              com filtros próprios: categoria, fases, classificação,
 *                              prioridade, status, responsável. Paridade com 'deal'.
 */
export interface CrmFilterTriggerConfig {
  // Tipo de entidade a filtrar
  entity_type: 'deal' | 'conversation' | 'task';
  
  
  // Filtros de hierarquia CRM (para deals)
  workspace_id?: string;
  workspace_name?: string;
  pipeline_id?: string;
  pipeline_name?: string;
  phase_ids?: string[];
  phase_names?: string[];
  
  // Filtros de atribuição (ambos: deal e conversation)
  // RELEASE: 1.2.0 - 2026-02-06 - Adicionado suporte a '__none__'
  // '__none__' = filtrar registros SEM atribuição (assigned_to IS NULL)
  seller_id?: string;
  seller_name?: string;
  assigned_to?: string | '__none__';
  assigned_to_name?: string;
  
  // Filtros de tags
  deal_tag_ids?: string[];
  deal_tag_names?: string[];
  contact_tag_ids?: string[];
  contact_tag_names?: string[];
  
  // Filtros de status para deals
  status?: 'open' | 'won' | 'lost';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  
  // =========================================================================
  // FILTROS ESPECÍFICOS PARA CONVERSAS (v1.1.0)
  // RELEASE: 1.2.0 - 2026-02-06 - Adicionado suporte a '__any__' e '__none__'
  // =========================================================================
  
  // Status da conversa: pendente, em aberto, encerrado ou qualquer
  // '__any__' = não aplica filtro de status (retorna todos)
  conversation_status?: 'pending' | 'open' | 'closed' | '__any__';
  
  // Departamento da conversa
  // '__none__' = filtrar conversas SEM departamento (department_id IS NULL)
  department_id?: string | '__none__';
  department_name?: string;
  
  // Kanban de conversas (categoria → fases)
  kanban_category_id?: string;
  kanban_category_name?: string;
  kanban_phase_ids?: string[];
  kanban_phase_names?: string[];
  
  // =========================================================================
  // FILTROS ESPECÍFICOS PARA TAREFAS (v1.3.0)
  // Todos opcionais. Aplicados somente quando entity_type === 'task'.
  // =========================================================================

  // Categoria da tarefa (cada categoria funciona como um Kanban independente)
  task_category_id?: string;
  task_category_name?: string;

  // Fases (colunas do Kanban) da categoria selecionada (multi-select)
  task_phase_ids?: string[];
  task_phase_names?: string[];

  // Classificação hierárquica da tarefa (opcional)
  task_classification_id?: string;
  task_classification_name?: string;

  // Prioridade da tarefa (enum public.task_priority)
  task_priority?: 'low' | 'normal' | 'high' | 'urgent';

  // Status da tarefa (enum public.task_status)
  // '__any__' = não aplica filtro de status
  task_status?: 'pending' | 'in_progress' | 'paused' | 'completed' | 'cancelled' | '__any__';

  // Responsável pela tarefa
  // '__none__' = filtrar tarefas SEM responsável (assigned_to IS NULL)
  task_assigned_to?: string | '__none__';
  task_assigned_to_name?: string;

  // =========================================================================
  // CONFIGURAÇÃO DE TEMPO (obrigatório)
  // =========================================================================

  
  // Configuração de tempo no status atual
  // Dispara se a entidade está no status/fase há mais tempo que o configurado
  time_in_status: number;
  time_unit: 'minutes' | 'hours' | 'days' | 'weeks';
  
  // Controle de execução
  execution_limit?: number; // Quantas vezes executar por entidade (padrão: 1)
}

/**
 * Tipo de origem de mensagem para filtro no gatilho active_conversation
 * 
 * RELEASE: 1.0.0 - 2024-12-30 - Novo tipo para filtro de origem
 */
export type MessageOrigin = 'campaign' | 'bot' | 'agent';

/**
 * Gatilho de conversa em andamento
 * Dispara quando já existe uma conversa ativa com agente nas últimas X horas
 * 
 * RELEASE: 1.0.0 - 2024-12-25 - Novo tipo de gatilho
 * RELEASE: 1.1.0 - 2024-12-26 - Corrigido: janela baseada na última mensagem, não created_at
 * RELEASE: 1.2.0 - 2024-12-30 - Adicionado ignore_origins para filtrar por origem da última mensagem
 */
export interface ActiveConversationTrigger {
  type: 'active_conversation';
  hours_window?: number; // Janela de tempo em horas (padrão: 24h). Se 0, qualquer conversa ativa dispara
  // NOVO: Origens a serem ignoradas pelo gatilho
  // Se a última mensagem ANTERIOR for de uma dessas origens, o gatilho NÃO dispara
  ignore_origins?: MessageOrigin[];
}

/**
 * Gatilho de qualquer mensagem (fallback universal)
 * Dispara para qualquer mensagem inbound quando nenhum outro gatilho casar
 * Deve ser configurado com prioridade baixa (ex: 999) para ser o último a ser verificado
 * 
 * RELEASE: 1.0.0 - 2024-12-26 - Novo tipo de gatilho
 */
export interface AnyMessageTrigger {
  type: 'any_message';
  priority?: number; // Prioridade (padrão: 999 = baixa, avaliado por último)
}

/**
 * Gatilho de palavra-chave
 */
export interface KeywordTrigger {
  type: 'keyword';
  keywords: string[];
  matchType: 'exact' | 'contains' | 'starts_with' | 'regex';
}

/**
 * Gatilho de primeira mensagem
 */
export interface FirstMessageTrigger {
  type: 'first_message';
  inboxIds?: string[];
}

/**
 * Gatilho agendado
 */
export interface ScheduledTrigger {
  type: 'scheduled';
  cronExpression: string;
  timezone: string;
}

/**
 * Gatilho de inatividade
 */
export interface InactivityTrigger {
  type: 'inactivity';
  durationMinutes: number;
  conversationStatus: 'open' | 'pending';
}

/**
 * Gatilho de evento do sistema
 * 
 * RELEASE: 1.1.0 - 2024-12-31 - Adicionado 'conversation_reopened'
 */
export interface EventTrigger {
  type: 'event';
  eventType: 'conversation_closed' | 'tag_added' | 'agent_assigned' | 'conversation_reopened';
  eventConfig?: Record<string, any>;
}

/**
 * Configuração do gatilho webhook integrado
 * Suporta dois modos: conversa (cria/reabre conversa) ou negócios (cria deal CRM)
 * 
 * RELEASE: 2.0.0 - 2026-01-30 - Substituído webhook legado por webhook integrado
 */
export interface WebhookTriggerConfig {
  // Tipo do gatilho (obrigatório para serialização)
  type: 'webhook';
  
  // Modo de operação (obrigatório)
  mode: 'conversation' | 'deal';
  
  // Mapeamento de campos do payload (obrigatório, mas campos internos opcionais)
  field_mapping: {
    contact_phone?: string;     // Caminho para telefone (ex: "telefone", "data.phone")
    contact_name?: string;      // Caminho para nome (ex: "nome", "signatario.nome")
    contact_email?: string;     // Caminho para email
    deal_title?: string;        // Título do deal (modo negócios)
    deal_tags?: string;         // Tags do deal separadas por vírgula
  };
  
  // Variáveis customizadas a extrair do payload
  variable_mapping?: Record<string, string>;
  
  // === MODO CONVERSA ===
  inbox_id?: string;            // ID da inbox destino
  inbox_name?: string;          // Nome para exibição
  
  // === MODO NEGÓCIOS ===
  workspace_id?: string;        // ID do workspace CRM
  workspace_name?: string;
  pipeline_id?: string;         // ID do pipeline
  pipeline_name?: string;
  phase_id?: string;            // ID da fase inicial
  phase_name?: string;
  default_source?: string;      // Origem do deal
  seller_id?: string;           // Vendedor fixo (opcional)
  seller_name?: string;
  
  // === AUTENTICAÇÃO (opcional) ===
  require_auth?: boolean;
  auth_header_name?: string;    // Ex: "X-Webhook-Secret"
  auth_header_value?: string;   // Valor esperado
}

/**
 * Gatilho de webhook integrado (type alias)
 * WebhookTriggerConfig agora inclui 'type' diretamente
 */
export type WebhookTrigger = WebhookTriggerConfig;

/**
 * Gatilho por tag do contato
 * Dispara quando contato possui, recebe ou perde uma tag
 */
export interface ContactTagTrigger {
  type: 'contact_tag';
  tag_ids: string[];
  condition: 'has_tag' | 'tag_added' | 'tag_removed';
}

/**
 * Gatilho por fase do kanban
 * Dispara quando contato entra ou está em determinada fase
 */
export interface PhaseTrigger {
  type: 'phase';
  phase_ids: string[];
  condition: 'entered' | 'is_in';
}

/**
 * Gatilho por departamento
 * Dispara quando conversa é atribuída ou está atribuída a um departamento
 */
export interface DepartmentTrigger {
  type: 'department';
  department_ids: string[];
  condition: 'assigned' | 'is_assigned';
}

/**
 * União de todos os tipos de trigger
 * 
 * RELEASE: 1.1.0 - 2024-12-25 - Adicionado ActiveConversationTrigger
 * RELEASE: 1.2.0 - 2024-12-26 - Adicionado AnyMessageTrigger (fallback universal)
 */
export type Trigger = 
  | KeywordTrigger
  | FirstMessageTrigger
  | ScheduledTrigger
  | InactivityTrigger
  | EventTrigger
  | WebhookTrigger
  | ContactTagTrigger
  | PhaseTrigger
  | DepartmentTrigger
  | ActiveConversationTrigger
  | AnyMessageTrigger;

/**
 * Status do fluxo
 */
export type FlowStatus = 'draft' | 'active' | 'paused' | 'archived';

/**
 * Status de execução
 */
export type ExecutionStatus = 'running' | 'completed' | 'failed' | 'paused' | 'cancelled' | 'waiting_input';

/**
 * Configuração base de trigger (frontend)
 */
export interface TriggerConfig {
  type: TriggerType;
  config: any;
  is_active: boolean;
  priority: number;
  inbox_ids: string[] | null; // null = todas as inboxes
}

/**
 * Variável do fluxo
 */
export interface FlowVariable {
  name: string;
  type: 'text' | 'number' | 'boolean' | 'date' | 'array' | 'object';
  defaultValue?: any;
  description?: string;
}

/**
 * Definição completa de um fluxo
 */
export interface Flow {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  status: FlowStatus;
  version: number;
  trigger_config: Record<string, any>;
  nodes: FlowNode[];
  edges: FlowEdge[];
  variables: FlowVariable[];
  settings: Record<string, any>;
  created_by?: string;
  created_at: string;
  updated_at: string;
  published_at?: string;
}

/**
 * Bloco disponível na sidebar para arrastar
 */
export interface BlockDefinition {
  type: NodeType;
  label: string;
  icon: any;
  color: string;
  description?: string;
}

/**
 * Categoria de blocos
 */
export interface BlockCategory {
  name: string;
  icon: any;
  blocks: BlockDefinition[];
}

// ============================================================================
// EXPORTAÇÃO/IMPORTAÇÃO DE FLUXOS
// RELEASE: 1.0.0 - 2026-02-03 - Sistema de export/import entre tenants
// ============================================================================

/**
 * Formato padronizado para exportação/importação de fluxos
 * Estrutura JSON que pode ser salva como arquivo e importada em outro tenant
 */
export interface FlowExportFormat {
  /** Versão do formato de exportação para compatibilidade futura */
  format_version: string;
  /** Data/hora da exportação em ISO 8601 */
  export_date: string;
  /** Aplicação de origem */
  source_app: string;
  /** Dados do fluxo */
  flow: {
    /** Nome do fluxo */
    name: string;
    /** Descrição opcional */
    description?: string;
    /** Nodes do fluxo (estrutura ReactFlow) */
    nodes: Array<{
      id: string;
      type: string;
      position: { x: number; y: number };
      data: {
        type: string;
        label?: string;
        config?: Record<string, unknown>;
        [key: string]: unknown;
      };
      [key: string]: unknown;
    }>;
    /** Edges/conexões do fluxo */
    edges: Array<{
      id: string;
      source: string;
      target: string;
      sourceHandle?: string;
      targetHandle?: string;
      [key: string]: unknown;
    }>;
    /** Variáveis definidas no fluxo */
    variables: Array<Record<string, unknown>>;
    /** Configurações gerais do fluxo */
    settings: Record<string, unknown>;
    /** Configuração de gatilhos */
    trigger_config?: Array<{
      type: string;
      [key: string]: unknown;
    }>;
  };
  /** Lista de referências externas que precisam ser mapeadas na importação */
  external_references: Array<{
    /** Placeholder no formato __REF:type:name__ */
    placeholder: string;
    /** Tipo da referência (agent, department, etc.) */
    type: string;
    /** UUID original (do tenant de origem) */
    originalId: string;
    /** Nome da entidade para match */
    name: string;
    /** Contexto onde a referência foi encontrada */
    context: string;
    /** ID do node onde a referência foi encontrada */
    nodeId?: string;
    /** Tipo do node */
    nodeType?: string;
  }>;
  
  /**
   * Metadados de variáveis referenciadas (para criação automática na importação)
   * RELEASE: 1.1.0 - 2026-02-03 - Suporte a variáveis globais
   */
  variable_metadata?: Array<{
    /** Placeholder da variável */
    placeholder: string;
    /** Nome interno da variável (slug) */
    name: string;
    /** Nome de exibição */
    display_name: string;
    /** Tipo da variável (text, number, etc.) */
    type: string;
    /** Se é variável de sistema */
    is_system: boolean;
    /** Descrição opcional */
    description?: string;
  }>;
}

/**
 * Configuração do node de geração automática de contrato
 * Cria contrato a partir de published_contract + variáveis do fluxo
 * 
 * RELEASE: 1.0.0 - 2026-03-22 - Novo node contract_generate
 */
export interface ContractGenerateConfig {
  /** ID do contrato publicado selecionado */
  published_contract_id: string;
  /** Nome do contrato publicado (para exibição no node) */
  published_contract_name?: string;
  /** Mapeamento: chave da variável do template → nome da variável do fluxo */
  variable_mapping: Record<string, string>;
  /** Signatários do contrato (dinâmicos via variáveis do fluxo) */
  signers: ContractGenerateSigner[];
  /** Modo de seleção do deal: from_trigger (do contexto CRM), from_variable (variável específica) ou none (sem negócio) */
  deal_selection: 'from_trigger' | 'from_variable' | 'none';
  /** Nome da variável que contém deal_id (quando deal_selection = from_variable) */
  deal_variable?: string;
  /** ID da variável global do tenant selecionada para deal_id */
  deal_variable_id?: string;
  /** Apelido do node */
  nickname?: string;
  /** Enviar automaticamente para assinatura após gerar PDF */
  auto_send_signature?: boolean;
  /** Nome da variável global para salvar resultado da execução */
  result_variable?: string;
  /** ID da variável global do tenant para resultado */
  result_variable_id?: string;
}

/**
 * Signatário configurado no node contract_generate
 * Os valores reais são resolvidos em runtime via variáveis do fluxo
 */
export interface ContractGenerateSigner {
  /** Variável do fluxo com nome do signatário (ex: "input_nome") */
  name_variable: string;
  /** Variável do fluxo com email do signatário */
  email_variable?: string;
  /** Variável do fluxo com telefone do signatário */
  phone_variable?: string;
  /** Papel do signatário (ex: "contratante", "contratado") */
  role: string;
  /** Método de entrega da assinatura */
  delivery_method: 'email' | 'whatsapp';
  /** Nome padrão pré-configurado no contrato publicado (usado se name_variable não for informada) */
  default_name?: string;
  /** Email padrão pré-configurado no contrato publicado */
  default_email?: string;
  /** Telefone padrão pré-configurado no contrato publicado */
  default_phone?: string;
  /** Indica se o signatário veio do template do contrato publicado (campos papel/entrega ficam desabilitados) */
  from_template?: boolean;
}

// ============================================================================
// Configuração do node charge_generate (Gerar Cobrança)
// RELEASE: 1.0.0 - 2026-03-22 - Novo node charge_generate
// ============================================================================

/**
 * Configuração do node Gerar Cobrança
 * Define origem do cliente, itens, pagamento e tipo de cobrança
 */
export interface ChargeGenerateConfig {
  /** Origem do cliente: do contato, do deal ou de variável */
  customer_selection: 'from_contact' | 'from_deal' | 'from_variable';
  /** Variável com customer_id (quando customer_selection = from_variable) */
  customer_variable?: string;
  /** ID da variável global (quando customer_selection = from_variable) */
  customer_variable_id?: string;

  /** Origem do deal: do gatilho CRM, de variável ou sem negócio */
  deal_selection: 'from_trigger' | 'from_variable' | 'none';
  /** Variável com deal_id (quando deal_selection = from_variable) */
  deal_variable?: string;
  /** ID da variável global (quando deal_selection = from_variable) */
  deal_variable_id?: string;

  /** Origem dos itens: do deal ou itens fixos pré-configurados */
  items_source: 'from_deal' | 'fixed';
  /** Itens fixos configurados no node (quando items_source = fixed) */
  fixed_items?: ChargeFixedItem[];

  /** ID do meio de pagamento selecionado */
  meio_pagamento_id: string;
  /** Nome do meio de pagamento (para exibição no node) */
  meio_pagamento_nome?: string;

  /** ID da conta corrente selecionada */
  conta_corrente_id: string;
  /** Nome da conta corrente (para exibição no node) */
  conta_corrente_nome?: string;

  /** Tipo de cobrança: única ou parcelada */
  tipo_cobranca: 'unica' | 'parcelada';
  /** Número de parcelas (1-12, relevante quando parcelada) */
  numero_parcelas: number;

  /** Modo de cálculo do vencimento */
  due_date_mode: 'days_after' | 'variable';
  /** Dias após execução (quando due_date_mode = days_after) */
  due_date_days?: number;
  /** Variável com data de vencimento (quando due_date_mode = variable) */
  due_date_variable?: string;
  /** ID da variável global de vencimento */
  due_date_variable_id?: string;

  /** Descrição da cobrança */
  descricao?: string;
  /** Observações internas */
  observacoes?: string;
  /** Apelido do node no canvas */
  nickname?: string;

  /** Variável para salvar resultado da execução (created, error, waiting_provider) */
  result_variable?: string;
  /** ID da variável global de resultado */
  result_variable_id?: string;

  /** Variável com CPF/CNPJ do cliente (obrigatória para cadastro no provider) */
  cpf_cnpj_variable?: string;
  /** ID da variável global de CPF/CNPJ */
  cpf_cnpj_variable_id?: string;
}

/**
 * Item fixo configurado no node charge_generate
 * Usado quando items_source = 'fixed'
 */
export interface ChargeFixedItem {
  /** ID do produto no catálogo CRM */
  product_id: string;
  /** Nome do produto (para exibição) */
  product_name: string;
  /** Descrição do item */
  descricao: string;
  /** Quantidade */
  quantidade: number;
  /** Valor unitário em centavos */
  valor_unitario: number;
  /** Desconto em centavos */
  desconto: number;
}

/**
 * Configuração do node Criar Tarefa (task_create)
 *
 * RELEASE: 1.0.0 - 2026-05-31 - Versão inicial. Cria uma tarefa no módulo Tarefas
 * a partir de um fluxo automatizado, vinculando opcionalmente a contato e negócio
 * do contexto da execução (gatilho CRM / conversa).
 *
 * A persistência usa a RPC `public.create_task(...)`, já existente, com
 * `p_tenant_hint = context.tenant_id`.
 */
export interface TaskCreateConfig {
  /** Apelido amigável do node */
  nickname?: string;

  // ============== DESTINO (obrigatórios) ==============
  /** Categoria da tarefa (task_categories) */
  category_id: string;
  category_name?: string;
  category_color?: string;
  /** Fase inicial da tarefa (task_phases) — pertence à categoria escolhida */
  phase_id: string;
  phase_name?: string;
  phase_color?: string;
  /** Classificação opcional (task_classifications) — pertence à categoria escolhida */
  classification_id?: string;
  classification_name?: string;
  classification_color?: string;

  // ============== TÍTULO ==============
  /** Como gerar o título da tarefa */
  title_mode: 'static' | 'from_variable' | 'auto';
  /** Texto fixo (suporta {{variaveis}}) — quando title_mode = 'static' */
  title_static?: string;
  /** Nome da variável — quando title_mode = 'from_variable' */
  title_variable?: string;
  /** ID da variável (VariablePickerCombobox) */
  title_variable_id?: string;

  // ============== DESCRIÇÃO (opcional) ==============
  description_mode?: 'none' | 'static' | 'from_variable';
  description_static?: string;
  description_variable?: string;
  description_variable_id?: string;

  // ============== PRIORIDADE ==============
  /** Prioridade da tarefa (default: 'normal'). Enum do banco: low|normal|high|urgent */
  priority?: 'low' | 'normal' | 'high' | 'urgent';

  // ============== VENCIMENTO (opcional) ==============
  due_date_mode?: 'none' | 'relative' | 'from_variable';
  /** Valor numérico do vencimento relativo (ex.: 2) */
  due_date_relative_value?: number;
  /** Unidade do vencimento relativo */
  due_date_relative_unit?: 'minutes' | 'hours' | 'days';
  /** Nome da variável contendo data ISO 8601 */
  due_date_variable?: string;
  due_date_variable_id?: string;

  // ============== RESPONSÁVEL ==============
  /**
   * Como definir o responsável (assigned_to em tasks):
   * - none: tarefa sem responsável
   * - manual: usuário fixo escolhido no dialog
   * - deal_seller: vendedor atribuído ao deal vinculado
   * - contact_seller: vendedor padrão do contato (se houver)
   * - from_variable: user_id contido em variável do fluxo
   */
  assignee_mode?: 'none' | 'manual' | 'deal_seller' | 'contact_seller' | 'from_variable';
  /** user_id do responsável (assignee_mode = 'manual') */
  assignee_id?: string;
  assignee_name?: string;
  assignee_variable?: string;
  assignee_variable_id?: string;

  // ============== VÍNCULOS ==============
  /** Vincula tarefa ao contato da conversa (default: true) */
  link_to_contact?: boolean;
  /**
   * Vínculo com negócio CRM:
   * - none: não vincular
   * - from_trigger: usa entity_id quando entity_type='deal' do gatilho CRM
   * - from_variable: usa ID de deal armazenado em variável
   */
  link_to_deal?: 'none' | 'from_trigger' | 'from_variable';
  deal_variable?: string;
  deal_variable_id?: string;

  // ============== SAÍDA ==============
  /** Variável para armazenar o task_id criado */
  output_variable?: string;
  output_variable_id?: string;

  // ============== CHECKLIST (opcional) ==============
  /**
   * Aplica um Template de Checklist à tarefa recém-criada (modo 'append').
   * Quando ausente/null, nenhum checklist é aplicado.
   */
  checklist_template_id?: string;
  /** Nome do template — apenas para exibição no card do node no builder */
  checklist_template_name?: string;
}


/**
 * Configuração do node Criar Atividade no Negócio (crm_create_activity)
 *
 * RELEASE: 1.0.0 - 2026-05-31 - Versão inicial. Registra automaticamente
 * uma atividade na timeline do negócio (crm_deal_activities) a partir
 * de um fluxo. Usa a RPC `create_deal_activity` — mesmo ponto único
 * usado pelo formulário manual do CRM.
 *
 * Esta v1 NÃO trata agendamento (scheduled_at = NULL sempre). Toda
 * atividade entra como registro histórico, sem aparecer na agenda.
 */
export interface CrmCreateActivityConfig {
  /** Apelido amigável do node */
  nickname?: string;

  // ============== NEGÓCIO DE DESTINO ==============
  /**
   * Como localizar o negócio:
   * - from_trigger: usa entity_id quando entity_type='deal' do contexto
   * - from_variable: usa ID de deal armazenado em variável
   */
  deal_source: 'from_trigger' | 'from_variable';
  /** Nome da variável que contém o deal_id (modo from_variable) */
  deal_variable?: string;
  deal_variable_id?: string;

  // ============== TIPO E CONTEÚDO ==============
  /** Tipo da atividade — whitelist do enum crm_activity_type */
  activity_type: 'note' | 'call' | 'email' | 'meeting' | 'task' | 'whatsapp' | 'visit';
  /** Título da atividade (obrigatório, aceita {{variaveis}}) */
  title: string;
  /** Descrição opcional (aceita {{variaveis}}) */
  description?: string;

  // ============== RESPONSÁVEL (performed_by) ==============
  /**
   * - none: sem responsável
   * - manual: usuário fixo selecionado no dialog
   * - deal_seller: vendedor atribuído ao deal (resolvido em runtime)
   * - from_variable: user_id em variável do fluxo
   */
  assignee_mode?: 'none' | 'manual' | 'deal_seller' | 'from_variable';
  assignee_id?: string;
  assignee_name?: string;
  assignee_variable?: string;
  assignee_variable_id?: string;

  // ============== SAÍDA ==============
  /** Variável para receber o activity_id criado */
  output_variable?: string;
  output_variable_id?: string;
}

/**
 * Configuração do node "Alterar Fase da Tarefa" (task_change_phase)
 *
 * RELEASE: 1.0.0 - 2026-05-31 - Versão inicial. Move uma tarefa existente
 * para outra fase da mesma categoria, via RPC `public.move_task_phase`
 * (com bypass híbrido auth.uid()/service_role para suporte ao Flow Engine).
 */
export interface TaskChangePhaseConfig {
  /** Apelido amigável do node */
  nickname?: string;

  // ============== TAREFA DE DESTINO ==============
  /**
   * Como localizar a tarefa:
   * - from_trigger: usa context.variables.task_id (gatilho de Tarefa)
   * - from_variable: usa task_id armazenado em variável do fluxo
   */
  task_source: 'from_trigger' | 'from_variable';
  task_variable?: string;
  task_variable_id?: string;

  // ============== FASE DE DESTINO ==============
  /** Categoria usada apenas para filtrar fases no dialog (validação server-side em runtime) */
  category_id: string;
  category_name?: string;
  category_color?: string;
  /** Fase de destino (task_phases) — deve pertencer à mesma categoria da tarefa */
  phase_id: string;
  phase_name?: string;
  phase_color?: string;
}

/**
 * Configuração do node "Atribuir Responsável da Tarefa" (task_assign)
 *
 * RELEASE: 1.0.0 - 2026-05-31 - Versão inicial. Define ou remove o
 * responsável de uma tarefa via RPC `public.update_task` (com bypass
 * híbrido auth.uid()/service_role para suporte ao Flow Engine).
 */
export interface TaskAssignConfig {
  /** Apelido amigável do node */
  nickname?: string;

  // ============== TAREFA DE DESTINO ==============
  task_source: 'from_trigger' | 'from_variable';
  task_variable?: string;
  task_variable_id?: string;

  // ============== RESPONSÁVEL ==============
  /**
   * - manual: usuário fixo selecionado no dialog
   * - deal_seller: vendedor atribuído ao deal vinculado à tarefa
   * - from_variable: user_id em variável do fluxo
   * - clear: remove o responsável atual (assigned_to = NULL)
   */
  assignee_mode: 'manual' | 'deal_seller' | 'from_variable' | 'clear';
  assignee_id?: string;
  assignee_name?: string;
  assignee_variable?: string;
  assignee_variable_id?: string;
}

// ============================================================================
// Configuração do node connection_query (Conexão)
// RELEASE: 1.0.0 - 2026-08-29 - Consulta assíncrona em provider integrado
// ============================================================================

/**
 * Node "Conexão": consulta um aplicativo integrado (Integration Hub).
 * A execução é enfileirada (fila q_integration) e o retorno chega ao fluxo em
 * variáveis: <result_variable>, _status, _pdf_url, _erro e _consulta_id.
 */
export interface ConnectionQueryConfig {
  /** Apelido exibido no node */
  nickname?: string;
  /** Conexão ativa do tenant */
  connection_id?: string;
  /** Nome da conexão (apenas para exibição) */
  connection_name?: string | null;
  /** Slug do provider (apenas para exibição/importação) */
  provider_slug?: string | null;
  /** Código do recurso consultado */
  operation?: string;
  /** Nome do recurso (apenas para exibição) */
  operation_name?: string | null;
  /** Parâmetros do recurso (aceitam {{variaveis}} do fluxo) */
  params?: Record<string, unknown>;
  /** Variável que recebe o retorno (padrão: "consulta") */
  result_variable?: string;
  /** ID da variável global selecionada (apenas para exibição no builder) */
  result_variable_id?: string;
  /**
   * Mapeamento genérico do payload → variáveis do fluxo (padrão n8n).
   * Provider-agnóstico: os caminhos vêm do retorno real do teste.
   */
  response_mapping?: ResponseFieldMapping[];
  /** Valores usados no botão "Testar consulta" (só no builder) */
  test_values?: Record<string, string>;
}

